import { GrandSelect, type GrandSelectProps, type GrandSelectSimpleOption } from 'ui'
import { useMemo } from 'react'
import lodashKeyBy from 'lodash/keyBy'
import { type CountryCode, type Language } from 'ecosystem'
import { countryNames } from 'shared-utils'

interface CheckoutCountrySelectProps<
  Option extends GrandSelectSimpleOption<CountryCode> = GrandSelectSimpleOption<CountryCode>
> {
  value?: string
  defaultValue?: string
  selectProps?: GrandSelectProps<Option>
  instanceId: string
  availableCountries?: CountryCode[]
  locale?: Language
}

export const CheckoutCountrySelect = <
  Option extends GrandSelectSimpleOption<CountryCode> = GrandSelectSimpleOption<CountryCode>
>({
  instanceId,
  value,
  defaultValue,
  availableCountries,
  locale: _locale,
  selectProps: { styles, ...selectProps } = {}
}: CheckoutCountrySelectProps<Option>) => {
  const { countriesOptions, countriesOptionsDict } = useMemo(() => {
    const locale = (_locale ?? document?.documentElement.lang ?? 'sv') as Language
    let options: Option[] = Object.entries(countryNames[locale]).map(
      ([countryCode, countryName]) =>
        ({
          label: countryName,
          value: countryCode as CountryCode
        }) as Option
    )

    if (availableCountries?.length) {
      options = options.filter((option) => availableCountries.includes(option.value))
    }

    options.sort((a, b) => a.label.localeCompare(b.label, locale))

    return {
      countriesOptions: options,
      countriesOptionsDict: lodashKeyBy(options, 'value')
    }
  }, [_locale, availableCountries])

  const defaultCountryOption = useMemo(
    () => countriesOptions.find((option) => option.value === defaultValue),
    [countriesOptions, defaultValue]
  )

  return (
    <GrandSelect<Option, false>
      instanceId={instanceId}
      options={countriesOptions}
      defaultValue={defaultCountryOption}
      {...(value ? { value: countriesOptionsDict[value] } : {})}
      menuPosition="fixed"
      styles={{
        container: (base) => ({
          ...base,
          width: '100%'
        }),
        ...styles
      }}
      {...selectProps}
    />
  )
}
